import { useCallback, useEffect, useState } from 'react';
import axios from '../../../axiosConfig';

/**
 * Finance — Phase 4.2. Wastage list + mutation hook.
 * Calls /api/finance/wastage. Returns items + a per-reason summary
 * so the tab header can render breakdown chips without a second
 * request. Numbers are coerced to Number so the page never
 * stringifies a Prisma Decimal.
 */
export default function useWastage({ from = null, to = null, reason = null } = {}) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({
    totalCount: 0,
    totalQuantity: 0,
    totalLoss: 0,
    byReason: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (reason) params.reason = reason;
      const res = await axios.get('/finance/wastage', { params });
      setItems(res.data?.items || []);
      const s = res.data?.summary || {};
      setSummary({
        totalCount: Number(s.totalCount || 0),
        totalQuantity: Number(s.totalQuantity || 0),
        totalLoss: Number(s.totalLoss || 0),
        byReason: Array.isArray(s.byReason)
          ? s.byReason.map((r) => ({
              reason: r.reason,
              count: Number(r.count || 0),
              quantity: Number(r.quantity || 0),
              totalLoss: Number(r.totalLoss || 0),
            }))
          : [],
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load wastage';
      setError(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [from, to, reason]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const create = useCallback(async (payload) => {
    const res = await axios.post('/finance/wastage', payload);
    const row = res.data?.wastage;
    if (row) {
      setItems((prev) => [row, ...prev]);
      // Locally bump the summary so the header reflects the new
      // entry without a refetch round-trip. The next refetch will
      // canonicalise.
      setSummary((prev) => ({
        ...prev,
        totalCount: prev.totalCount + 1,
        totalQuantity: prev.totalQuantity + Number(row.quantity || 0),
        totalLoss: prev.totalLoss + Number(row.totalLoss || 0),
      }));
    }
    return row;
  }, []);

  const remove = useCallback(async (id) => {
    await axios.delete(`/finance/wastage/${id}`);
    const removed = items.find((it) => it.id === id);
    setItems((prev) => prev.filter((it) => it.id !== id));
    if (removed) {
      setSummary((prev) => ({
        ...prev,
        totalCount: Math.max(prev.totalCount - 1, 0),
        totalQuantity: Math.max(prev.totalQuantity - Number(removed.quantity || 0), 0),
        totalLoss: Math.max(prev.totalLoss - Number(removed.totalLoss || 0), 0),
      }));
    }
  }, [items]);

  return {
    items,
    summary,
    loading,
    error,
    refetch: fetchAll,
    create,
    remove,
  };
}
